// src/components/schedules/SchedulesRecipients.tsx
import { useEffect, useMemo, useState } from "react";
import { Loader2, Save, Search, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EmailChipsInput } from "@/components/EmailChipsInput";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { useReportRecipients, useReportList, useRecipientEmails, useSetReportRecipients } from "@/hooks/useReportDelivery";

export function SchedulesRecipients() {
  const { data: recipients, isLoading: recipientsLoading } = useReportRecipients();
  const { data: reports, isLoading: reportsLoading } = useReportList({ active: true });
  const { data: emailList } = useRecipientEmails();
  const setRecipients = useSetReportRecipients();

  const isLoading = recipientsLoading || reportsLoading;

  const [reportSearch, setReportSearch] = useState("");
  const [emailSearch, setEmailSearch] = useState("");
  const [selectedReportID, setSelectedReportID] = useState<number | null>(null);

  // Emails à ajouter (helper)
  const [emailsToAdd, setEmailsToAdd] = useState<string[]>([]);

  const allEmails = useMemo(() => {
    const fromApi = (emailList || []).map((e) => (e || "").trim().toLowerCase()).filter(Boolean);
    const fromAssoc = (recipients || []).map((r) => (r.dest || "").trim().toLowerCase()).filter(Boolean);
    return Array.from(new Set([...fromApi, ...fromAssoc])).sort();
  }, [emailList, recipients]);

  const filteredReports = useMemo(() => {
    const q = reportSearch.trim().toLowerCase();
    const list = (reports || []).slice();
    if (!q) return list;
    return list.filter((r) => `${r.reportID} ${r.name}`.toLowerCase().includes(q));
  }, [reports, reportSearch]);

  const reportRecipientsSet = useMemo(() => {
    const map = new Map<number, Set<string>>();
    (recipients || []).forEach((r) => {
      const rid = r.reportID;
      const dest = (r.dest || "").trim().toLowerCase();
      if (!rid || !dest) return;
      if (!map.has(rid)) map.set(rid, new Set());
      map.get(rid)!.add(dest);
    });
    return map;
  }, [recipients]);

  // Init report selection
  useEffect(() => {
    if (selectedReportID !== null) return;
    const first = filteredReports?.[0]?.reportID;
    if (first) setSelectedReportID(first);
  }, [filteredReports, selectedReportID]);

  const initialChecked = useMemo(() => {
    if (!selectedReportID) return new Set<string>();
    return new Set(reportRecipientsSet.get(selectedReportID) || []);
  }, [reportRecipientsSet, selectedReportID]);

  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [dirty, setDirty] = useState(false);

  // Reset checked when changing report
  useEffect(() => {
    setChecked(new Set(initialChecked));
    setDirty(false);
    setEmailsToAdd([]);
    setEmailSearch("");
  }, [selectedReportID, initialChecked]);

  const filteredEmails = useMemo(() => {
    const q = emailSearch.trim().toLowerCase();
    if (!q) return allEmails;
    return allEmails.filter((e) => e.includes(q));
  }, [allEmails, emailSearch]);

  const selectedReport = useMemo(
    () => (selectedReportID ? reports?.find((r) => r.reportID === selectedReportID) : undefined),
    [reports, selectedReportID],
  );

  const toggleEmail = (email: string, on: boolean) => {
    const e = (email || "").trim().toLowerCase();
    if (!e) return;
    setChecked((prev) => {
      const next = new Set(prev);
      if (on) next.add(e);
      else next.delete(e);
      return next;
    });
    setDirty(true);
  };

  const addEmailsToSelection = () => {
    if (emailsToAdd.length === 0) return;
    const normalized = emailsToAdd.map((e) => (e || "").trim().toLowerCase()).filter(Boolean);
    setChecked((prev) => {
      const next = new Set(prev);
      normalized.forEach((e) => next.add(e));
      return next;
    });
    setDirty(true);
    setEmailsToAdd([]);
  };

  const selectAll = () => {
    setChecked(new Set(allEmails));
    setDirty(true);
  };

  const selectNone = () => {
    setChecked(new Set());
    setDirty(true);
  };

  const handleSave = async () => {
    if (!selectedReportID) return;
    const dests = Array.from(checked).sort();
    await setRecipients.mutateAsync({ reportID: selectedReportID, dests });
    setDirty(false);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Destinataires</CardTitle>
        <CardDescription>Choisissez un rapport puis cochez qui le reçoit</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-center py-10 text-muted-foreground">Chargement...</div>
        ) : !reports || reports.length === 0 ? (
          <div className="text-center py-10 text-muted-foreground">Aucun rapport disponible.</div>
        ) : (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-[360px_1fr]">
            {/* Colonne gauche: rapports */}
            <div className="space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={reportSearch}
                  onChange={(e) => setReportSearch(e.target.value)}
                  placeholder="Rechercher un rapport..."
                  className="pl-9"
                />
              </div>
              <ScrollArea className="h-[480px] rounded-md border">
                <div className="p-2 space-y-1">
                  {filteredReports.map((r) => {
                    const count = reportRecipientsSet.get(r.reportID)?.size || 0;
                    const active = r.reportID === selectedReportID;
                    return (
                      <button
                        key={r.reportID}
                        type="button"
                        onClick={() => setSelectedReportID(r.reportID)}
                        className={[
                          "w-full text-left rounded-md px-3 py-2 transition",
                          active ? "bg-muted" : "hover:bg-muted/60",
                        ].join(" ")}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <div className="truncate font-medium">{r.name}</div>
                            <div className="text-xs text-muted-foreground">#{r.reportID}</div>
                          </div>
                          <div className="text-xs text-muted-foreground whitespace-nowrap">{count} dest.</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </ScrollArea>
            </div>

            {/* Colonne droite: destinataires du rapport */}
            <div className="space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm text-muted-foreground">Rapport sélectionné</div>
                  <div className="truncate text-lg font-semibold">{selectedReport?.name || "-"}</div>
                </div>
                <Button
                  onClick={handleSave}
                  disabled={!selectedReportID || !dirty || setRecipients.isPending}
                  className="shrink-0"
                >
                  {setRecipients.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Enregistrement...
                    </>
                  ) : (
                    <>
                      <Save className="mr-2 h-4 w-4" />
                      Enregistrer
                    </>
                  )}
                </Button>
              </div>

              <Separator />

              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <UserPlus className="h-4 w-4" />
                  Ajouter des destinataires (puis “Ajouter au rapport”)
                </Label>
                <EmailChipsInput value={emailsToAdd} onChange={setEmailsToAdd} placeholder="Collez/ajoutez des emails..." />
                <div className="flex items-center gap-2">
                  <Button type="button" variant="secondary" onClick={addEmailsToSelection} disabled={emailsToAdd.length === 0}>
                    Ajouter au rapport
                  </Button>
                  <div className="text-xs text-muted-foreground">
                    {Array.from(checked).length} sélectionné(s)
                  </div>
                </div>
              </div>

              <Separator />

              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="relative w-full sm:max-w-md">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={emailSearch}
                    onChange={(e) => setEmailSearch(e.target.value)}
                    placeholder="Rechercher un destinataire..."
                    className="pl-9"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Button type="button" variant="outline" onClick={selectAll} disabled={allEmails.length === 0}>
                    Tout cocher
                  </Button>
                  <Button type="button" variant="outline" onClick={selectNone}>
                    Tout décocher
                  </Button>
                </div>
              </div>

              <ScrollArea className="h-[380px] rounded-md border">
                <div className="p-3 space-y-2">
                  {filteredEmails.length === 0 ? (
                    <div className="text-sm text-muted-foreground">Aucun destinataire.</div>
                  ) : (
                    filteredEmails.map((email) => (
                      <div key={email} className="flex items-center gap-2 rounded-md px-2 py-1 hover:bg-muted/50">
                        <Checkbox
                          id={`dest-${email}`}
                          checked={checked.has(email)}
                          onCheckedChange={(v) => toggleEmail(email, v === true)}
                        />
                        <Label htmlFor={`dest-${email}`} className="text-sm font-normal cursor-pointer flex-1 truncate">
                          {email}
                        </Label>
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

